import type { HtmlAttributes } from '@studio/design-system/props';
import * as Tabs from '@studio/design-system/tabs';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'cn';
import { HRule } from '../layout.js';

/**
 * Props for the ScrollableMainTabs component
 */
export interface ScrollableMainTabsProps extends HtmlAttributes {
  /** The tab triggers to render */
  children: ReactNode;
}

/**
 * Component for displaying horizontally scrollable main tabs with fade-out effect
 */
export const ScrollableMainTabs = ({
  className,
  children,
  ...props
}: ScrollableMainTabsProps): ReactElement => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const rafRef = useRef<number | null>(null);

  // Update fade visibility based on scroll position
  const updateFadeVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const threshold = 5;

    const shouldShowLeft = scrollLeft > threshold;
    const shouldShowRight = scrollLeft < maxScroll - threshold;

    setShowLeftFade(shouldShowLeft);
    setShowRightFade(shouldShowRight);
  }, []);

  // Throttled scroll handler using requestAnimationFrame
  const handleScroll = useCallback(() => {
    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      updateFadeVisibility();
      rafRef.current = null;
    });
  }, [updateFadeVisibility]);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) return;

      // Convert vertical wheel to horizontal scroll if no horizontal wheel movement
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
        // Manually trigger our scroll handler since wheel events might not trigger scroll events
        handleScroll();
      }
    },
    [handleScroll]
  );

  const handleTouchMove = useCallback(() => {
    // Touch scrolling naturally triggers scroll events, but we ensure our handler runs
    handleScroll();
  }, [handleScroll]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Check if horizontal scrolling is needed
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) return;

      const scrollAmount = 100; // Pixels to scroll per key press

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        container.scrollLeft -= scrollAmount;
        handleScroll();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        container.scrollLeft += scrollAmount;
        handleScroll();
      }
    },
    [handleScroll]
  );

  // Set up scroll, wheel, touch, and keyboard event listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    // Make container focusable for keyboard navigation
    container.setAttribute('tabindex', '0');

    // Initial check
    updateFadeVisibility();

    container.addEventListener('scroll', handleScroll, { passive: true, signal });
    container.addEventListener('wheel', handleWheel, { passive: false, signal });
    container.addEventListener('touchmove', handleTouchMove, { passive: true, signal });
    container.addEventListener('keydown', handleKeyDown, { passive: false, signal });

    // Clean up
    return () => {
      abortController.abort();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [handleScroll, updateFadeVisibility]);

  return (
    <div className={twMerge('flex w-full flex-col', className)} {...props}>
      <div className='relative w-full'>
        {/* Scrollable container with text-only fade-out effect */}
        <div className='relative'>
          {/* Scrollable container with mask-based fade effects */}
          <div
            ref={scrollContainerRef}
            className={twMerge(
              'scrollbar-hide flex w-full overflow-x-auto',
              // Apply mask-based fade effects based on scroll state
              showLeftFade && showRightFade && 'mask-fade-both',
              showLeftFade && !showRightFade && 'mask-fade-left',
              !showLeftFade && showRightFade && 'mask-fade-right'
            )}
          >
            <Tabs.List className='mx-8 mb-18 gap-28 overflow-visible whitespace-nowrap border-0 bg-transparent px-0 text-md'>
              {children}
            </Tabs.List>
          </div>
          <HRule className='my-0 -mt-18 mb-32' />
        </div>
      </div>
    </div>
  );
};
